import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { useKeycloak } from "@react-keycloak/web";
import "./index.css";

import AssessmentApp from "modules/assessment/AssessmentApp";
import StudentsApp from "modules/studenthub/StudentsApp";
import HomePage from "./modules/HomePage";

import SessionTimeoutManager from "shared/security/SessionTimeoutManager";
import Toast from "shared/common/Toast";
import { I18nProvider } from "shared/contexts/I18nContext";
import { ProtectedRoute } from "shared/security/ProtectedRoute";
import HomeApp from "shared/common/Home";

import useInitializeUserData from "shared/hooks/useInitializeUserData";
import EnrollmentApp from "modules/EnrollmentPage";

// Adaptive Learning Imports
import AdaptiveLearningDashboard from "modules/adaptivelearning/AdaptiveLearningDashboard";
import LearningSession from "modules/adaptivelearning/LearningSession";
import ResultsPage from "modules/adaptivelearning/ResultsPage";

export const AccessDenied = () => {
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (window.history.length > 1) {
        navigate(-1);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [navigate]);

  return (
    <div className="flex items-center justify-center h-screen bg-gray-100">
      <div className="bg-white shadow-lg rounded-lg p-8 text-center max-w-md">
        <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
        <p className="text-gray-700 mb-2">
          You do not have permission to view{" "}
          <code className="bg-gray-200 px-2 py-1 rounded">
            {location.pathname}
          </code>
          .
        </p>
        <p className="text-sm text-gray-500">Returning to previous page...</p>
      </div>
    </div>
  );
};

const App = () => {
  const { keycloak, initialized } = useKeycloak();
  const { loading, error, ready } = useInitializeUserData(keycloak);

  const [toastMessage, setToastMessage] = React.useState("");
  const [toastType, setToastType] = React.useState("info");
  const [showToast, setShowToast] = React.useState(false);

  const userId = null;

  const displayToast = (message, type = "info") => {
    if (typeof message === "object") {
      setToastMessage(message.message);
      setToastType(message.type);
      setShowToast(true);
    } else {
      setToastMessage(message);
      setToastType(type);
      setShowToast(true);
    }
  };



  const hideToast = () => {
    setShowToast(false);
    setToastMessage("");
    setToastType("info");
  };

  if (!initialized || (keycloak.authenticated && loading)) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <div className="text-center">
          <div className="loader mb-4" />
          <p className="text-gray-700 text-lg">Setting up your session...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-red-100">
        <div className="bg-white shadow-lg rounded-lg p-8 text-center max-w-md">
          <h2 className="text-2xl font-bold text-red-600 mb-4">
            Initialization Failed
          </h2>
          <p className="text-gray-700 mb-2">
            We couldn't load your profile data. Please try logging in again.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Don't show session timeout in popup windows (like assessment window) */}
      {!window.opener && (
        <SessionTimeoutManager timeoutMinutes={90} warningMinutes={5} />
      )}
      <Router>
        <Routes>
          {/* Public route */}
          <Route
            path="/"
            element={
              keycloak.authenticated ? (
                ready && <Navigate to="/home" />
              ) : (
                <HomePage />
              )
            }
          />

          {/* Protected routes */}
          <Route
            path="/home"
            element={
              <ProtectedRoute
                requiredRoles={["ROLE_TEACHER", "ROLE_PARENT", "ROLE_STUDENT"]}
              >
                <HomeApp displayToast={displayToast} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/studentHub"
            element={
              <ProtectedRoute requiredRoles={["ROLE_STUDENT"]}>
                <EnrollmentApp
                  curPage='studentHub'
                  studentId={userId}
                  displayToast={displayToast}
                />
              </ProtectedRoute>
            }
          />

          <Route
            path="/assessment"
            element={
              <ProtectedRoute requiredRoles={["ROLE_TEACHER", "ROLE_STUDENT"]}>
                <AssessmentApp />
              </ProtectedRoute>
            }
          />
          <Route
            path="/student"
            element={
              <ProtectedRoute
                requiredRoles={["ROLE_TEACHER", "ROLE_STUDENT", "ROLE_PARENT"]}
              >
                <StudentsApp displayToast={displayToast} />
              </ProtectedRoute>
            }
          />

          {/* Adaptive Learning Routes - Now integrated into StudentHub */}
          {/* Keeping these routes for direct access if needed */}
          <Route
            path="/adaptive-learning"
            element={
              <ProtectedRoute requiredRoles={["ROLE_STUDENT"]}>
                <AdaptiveLearningDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/adaptive-learning/session/:conceptCode"
            element={
              <ProtectedRoute requiredRoles={["ROLE_STUDENT"]}>
                <LearningSession />
              </ProtectedRoute>
            }
          />
          <Route
            path="/adaptive-learning/results/:sessionId"
            element={
              <ProtectedRoute requiredRoles={["ROLE_STUDENT"]}>
                <ResultsPage />
              </ProtectedRoute>
            }
          />

          {/* Access Denied Route */}
          <Route path="/access-denied" element={<AccessDenied />} />
        </Routes>
      </Router>
      {showToast && (
        <I18nProvider>
          <Toast
            message={toastMessage}
            type={toastType}
            onClose={hideToast}
            duration={5000}
          />
        </I18nProvider>
      )}
    </>
  );
};

export default App;
